import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/auth-context";
import { ThemeProvider } from "./contexts/theme-context";
import { Toaster } from "./components/ui/sonner";
import { AbilityProvider } from "./components/ability-provider";
import { RumIdentitySync } from "./components/rum-identity-sync";
import { ImpersonationCacheSync } from "./components/impersonation-cache-sync";
import { ProtectedRoute } from "./components/protected-route";
import { WelcomeGuard } from "./components/welcome-guard";
import WelcomePage from "./pages/v2/welcome";
import LoginPage from "./pages/login";
import SignupPage from "./pages/signup";
import ForgotPasswordPage from "./pages/forgot-password";
import ResetPasswordPage from "./pages/reset-password";
import AuthCallbackPage from "./pages/auth/callback";
import OAuthAuthorizePage from "./pages/oauth/authorize";

import BrandPage from "./pages/brand";
import BrandEmailPage from "./pages/brand-email";
import V2SettingsDataImportPage from "./pages/v2/settings/data-import";
import IntegrationsPage from "./pages/v2/settings/integrations";
import { AbilityRoute } from "./components/ability-route";
import NoAccountPage from "./pages/no-account";
import AppLayoutV2 from "./components/layout/app-layout-v2";
import V2DashboardPage from "./pages/v2/dashboard/index";
import V2TodayPage from "./pages/v2/today/index";
import V2OpportunitiesPage from "./pages/v2/opportunities/index";
import V2OpportunityDetailPage from "./pages/v2/opportunities/detail";
import V2OpportunityCreatePage from "./pages/v2/opportunities/create";
import V2OpportunityEditPage from "./pages/v2/opportunities/edit";
import V2CustomersPage from "./pages/v2/customers/index";
import V2CustomerDetailPage from "./pages/v2/customers/detail";
import V2CustomerCreatePage from "./pages/v2/customers/create";
import V2CustomerEditPage from "./pages/v2/customers/edit";
import V2ContactsPage from "./pages/v2/contacts/index";
import V2ContactDetailPage from "./pages/v2/contacts/detail";
import V2InvoicesPage from "./pages/v2/invoices/index";
import V2InvoiceDetailPage from "./pages/v2/invoices/detail";
import V2InvoiceCreatePage from "./pages/v2/invoices/create";
import V2InvoiceEditPage from "./pages/v2/invoices/edit";
import V2TransactionsPage from "./pages/v2/transactions/index";
import V2TransactionDetailPage from "./pages/v2/transactions/detail";
import V2JobsPage from "./pages/v2/jobs/index";
import V2JobDetailPage from "./pages/v2/jobs/detail";
import V2JobCreatePage from "./pages/v2/jobs/create";
import V2JobEditPage from "./pages/v2/jobs/edit";
import V2ImportPage from "./pages/v2/import/index";
import V2ImportReviewPage from "./pages/v2/import/jobs/review";
import V2SettingsGeneralPage from "./pages/v2/settings/general";
import V2SettingsTeamPage from "./pages/v2/settings/team";
import V2SettingsBrandsPage from "./pages/v2/settings/brands";
import V2SettingsApiKeysPage from "./pages/v2/settings/api-keys";
import V2SettingsWebhooksPage from "./pages/v2/settings/webhooks";
import V2SettingsConnectedAccountsPage from "./pages/v2/settings/connected-accounts";
import V2SettingsPreferencesPage from "./pages/v2/settings/preferences";
import V2SettingsProfilePage from "./pages/v2/settings/profile";
import V2SettingsAccountPage from "./pages/v2/settings/account";
import V2SettingsMembersPage from "./pages/v2/settings/members";
import V2SettingsMemberDetailPage from "./pages/v2/settings/member-detail";
import V2SettingsPermissionsPage from "./pages/v2/settings/permissions";
import V2AdvaAccountsPage from "./pages/v2/settings/adva/accounts";
import V2AdvaAccountUsersPage from "./pages/v2/settings/adva/account-users";
import V2AdvaAccountBusinessesPage from "./pages/v2/settings/adva/account-businesses";
import V2AdvaUsersPage from "./pages/v2/settings/adva/users";
import V2AdvaImpersonatePage from "./pages/v2/settings/adva/impersonate";
import V2SettingsDemoCatalogPage from "./pages/v2/settings/demo-import";
import V2SettingsDemoCatalogDetailPage from "./pages/v2/settings/demo-catalog-detail";
import V2SettingsBackupResetPage from "./pages/v2/settings/backup-reset";
import V2SettingsExplorePage from "./pages/v2/settings/explore";
import V2DevTypographyPage from "./pages/v2/dev/typography";
import V2DevAbilitiesPage from "./pages/v2/dev/abilities";
import V2TeamMembersPage from "./pages/v2/team-members/index";
import V2TeamMemberDetailPage from "./pages/v2/team-members/detail";
import V2TeamMemberCreatePage from "./pages/v2/team-members/create";
import V2TeamMemberEditPage from "./pages/v2/team-members/edit";
import SettingsBillingPage from "./pages/v2/settings/billing";
import V2SettingsLayout from "./pages/v2/settings/layout";
import V2SettingsBusinessesPage from "./pages/v2/settings/businesses";
import V2CrewsPage from "./pages/v2/crews/index";
import V2CrewCreatePage from "./pages/v2/crews/create";
import V2CrewEditPage from "./pages/v2/crews/edit";
import V2ProductsPage from "./pages/v2/products/index";
import V2ProductDetailPage from "./pages/v2/products/detail";
import V2ProposalsPage from "./pages/v2/proposals/index";
import V2ProposalDetailPage from "./pages/v2/proposals/detail";
import V2AppointmentsPage from "./pages/v2/appointments/index";
import V2AppointmentDetailPage from "./pages/v2/appointments/detail";
import V2LocationsPage from "./pages/v2/locations/index";
import V2LocationDetailPage from "./pages/v2/locations/detail";
import V2TerritoriesPage from "./pages/v2/territories/index";
import V2TerritoryDetailPage from "./pages/v2/territories/detail";
import V2WarrantiesPage from "./pages/v2/warranties/index";
import V2WarrantyDetailPage from "./pages/v2/warranties/detail";
import V2WarrantyClaimDetailPage from "./pages/v2/warranty-claims/detail";
// ADV-1176 / ADR-0048: service/maintenance agreements register + detail + plans.
import V2ServiceAgreementsPage from "./pages/v2/service-agreements/index";
import V2ServiceAgreementDetailPage from "./pages/v2/service-agreements/detail";
import V2ServicePlansPage from "./pages/v2/service-plans/index";
import V2ReportsPage from "./pages/v2/reports/index";
import V2CommissionDetailPage from "./pages/v2/commissions/detail";
// ADV-805: Ask Adva placeholder. Real chat surface lands with ADV-803.
import V2AskAdvaPage from "./pages/v2/ask-adva/index";
import AcceptInvitePage from "./pages/accept-invite";
import { initPostHog } from "./lib/posthog";

initPostHog();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <AbilityProvider>
            {/* ADV-1319: sync the authenticated user + account/business context
                into Dash0 RUM. Render-less; inside auth + query providers. */}
            <RumIdentitySync />
            {/* ADV-1460: render-less, mounted once above the router so cross-tab
                impersonation changes flush the query cache on every protected
                tree (main shell + settings shell) without per-consumer fan-out. */}
            <ImpersonationCacheSync />
            <Toaster position="top-right" richColors />
            <Router>
              <Routes>
                {/* Accept invite (public, standalone dark page) */}
                <Route path="/accept-invite" element={<AcceptInvitePage />} />

                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route
                  path="/oauth/authorize"
                  element={<OAuthAuthorizePage />}
                />
                <Route path="/no-account" element={<NoAccountPage />} />
                <Route path="/brand" element={<BrandPage />} />
                <Route path="/brand/email" element={<BrandEmailPage />} />
                {/* /terms now handled in MarketingLayout group */}
                <Route
                  path="/dev/typography"
                  element={<V2DevTypographyPage />}
                />
                <Route path="/dev/abilities" element={<V2DevAbilitiesPage />} />
                {/* Bookmark redirect: /v2/* → /* (ADV-655). */}
                <Route path="/v2/*" element={<V2RedirectAll />} />
                {/* First-run onboarding fork (ADV-1280) — full-screen, no sidebar */}
                <Route
                  path="/welcome"
                  element={
                    <ProtectedRoute>
                      <WelcomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <WelcomeGuard>
                        <AppLayoutV2 />
                      </WelcomeGuard>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<V2DashboardPage />} />
                  <Route path="today" element={<V2TodayPage />} />
                  <Route
                    path="opportunities"
                    element={<V2OpportunitiesPage />}
                  />
                  {/* ADV-1359: the board is an in-place view now. Redirect the
                      retired /opportunities/board route (and any bookmark or
                      history entry of it) to the unified page — it must precede
                      the dynamic :id route, which would otherwise treat "board"
                      as an opportunity id and load a broken detail page. */}
                  <Route
                    path="opportunities/board"
                    element={<Navigate to="/opportunities" replace />}
                  />
                  <Route
                    path="opportunities/new"
                    element={<V2OpportunityCreatePage />}
                  />
                  <Route
                    path="opportunities/:id"
                    element={<V2OpportunityDetailPage />}
                  />
                  <Route
                    path="opportunities/:id/edit"
                    element={<V2OpportunityEditPage />}
                  />
                  <Route path="customers" element={<V2CustomersPage />} />
                  <Route
                    path="customers/new"
                    element={<V2CustomerCreatePage />}
                  />
                  <Route
                    path="customers/:id"
                    element={<V2CustomerDetailPage />}
                  />
                  <Route
                    path="customers/:id/edit"
                    element={<V2CustomerEditPage />}
                  />
                  <Route path="contacts" element={<V2ContactsPage />} />
                  <Route
                    path="contacts/:id"
                    element={<V2ContactDetailPage />}
                  />
                  <Route path="invoices" element={<V2InvoicesPage />} />
                  <Route
                    path="invoices/new"
                    element={<V2InvoiceCreatePage />}
                  />
                  <Route
                    path="invoices/:id"
                    element={<V2InvoiceDetailPage />}
                  />
                  <Route
                    path="invoices/:id/edit"
                    element={<V2InvoiceEditPage />}
                  />
                  <Route path="transactions" element={<V2TransactionsPage />} />
                  <Route
                    path="transactions/:id"
                    element={<V2TransactionDetailPage />}
                  />
                  <Route path="jobs" element={<V2JobsPage />} />
                  <Route path="jobs/new" element={<V2JobCreatePage />} />
                  <Route path="jobs/:id" element={<V2JobDetailPage />} />
                  <Route path="jobs/:id/edit" element={<V2JobEditPage />} />
                  <Route path="import" element={<V2ImportPage />} />
                  <Route
                    path="import/jobs/:id"
                    element={<V2ImportReviewPage />}
                  />
                  <Route path="team-members" element={<V2TeamMembersPage />} />
                  <Route
                    path="team-members/new"
                    element={<V2TeamMemberCreatePage />}
                  />
                  <Route
                    path="team-members/:id"
                    element={<V2TeamMemberDetailPage />}
                  />
                  <Route
                    path="team-members/:id/edit"
                    element={<V2TeamMemberEditPage />}
                  />
                  <Route path="crews" element={<V2CrewsPage />} />
                  <Route path="crews/new" element={<V2CrewCreatePage />} />
                  <Route path="crews/:id/edit" element={<V2CrewEditPage />} />
                  <Route path="products" element={<V2ProductsPage />} />
                  <Route
                    path="products/:id"
                    element={<V2ProductDetailPage />}
                  />
                  <Route path="proposals" element={<V2ProposalsPage />} />
                  <Route
                    path="proposals/:id"
                    element={<V2ProposalDetailPage />}
                  />
                  <Route path="appointments" element={<V2AppointmentsPage />} />
                  <Route
                    path="appointments/:id"
                    element={<V2AppointmentDetailPage />}
                  />
                  <Route path="locations" element={<V2LocationsPage />} />
                  <Route
                    path="locations/:id"
                    element={<V2LocationDetailPage />}
                  />
                  <Route path="territories" element={<V2TerritoriesPage />} />
                  <Route
                    path="territories/:id"
                    element={<V2TerritoryDetailPage />}
                  />
                  <Route path="warranties" element={<V2WarrantiesPage />} />
                  <Route
                    path="warranties/:id"
                    element={<V2WarrantyDetailPage />}
                  />
                  {/* ADV-1171: warranty CLAIM detail relocated out of /warranties/:id
                      so that page can host the obligations register per ADR-0047. */}
                  <Route
                    path="warranty-claims/:id"
                    element={<V2WarrantyClaimDetailPage />}
                  />
                  {/* ADV-1176 / ADR-0048: service agreements register + detail,
                      plus the plan-management register. */}
                  <Route
                    path="service-agreements"
                    element={<V2ServiceAgreementsPage />}
                  />
                  <Route
                    path="service-agreements/:id"
                    element={<V2ServiceAgreementDetailPage />}
                  />
                  <Route
                    path="service-plans"
                    element={<V2ServicePlansPage />}
                  />
                  <Route path="reports" element={<V2ReportsPage />} />
                  {/* Commissions detail (ADV-1147); list page lands via ADV-797 */}
                  <Route
                    path="commissions/:id"
                    element={<V2CommissionDetailPage />}
                  />
                  {/* ADV-805: Ask Adva placeholder. The ⌘K palette routes
                      here; real chat surface ships with ADV-803. */}
                  <Route path="ask-adva" element={<V2AskAdvaPage />} />
                </Route>
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute>
                      <WelcomeGuard>
                        <V2SettingsLayout />
                      </WelcomeGuard>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="general" replace />} />
                  <Route path="profile" element={<V2SettingsProfilePage />} />
                  <Route path="general" element={<V2SettingsGeneralPage />} />
                  <Route path="team" element={<V2SettingsTeamPage />} />
                  <Route path="brands" element={<V2SettingsBrandsPage />} />
                  <Route
                    path="preferences"
                    element={<V2SettingsPreferencesPage />}
                  />
                  <Route path="api-keys" element={<V2SettingsApiKeysPage />} />
                  <Route
                    path="webhooks"
                    element={
                      <AbilityRoute action="manage" subject="WebhookEndpoint">
                        <V2SettingsWebhooksPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="connected-accounts"
                    element={<V2SettingsConnectedAccountsPage />}
                  />
                  <Route path="integrations" element={<IntegrationsPage />} />
                  <Route
                    path="billing"
                    element={
                      <AbilityRoute action="update" subject="Account">
                        <SettingsBillingPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="data-import"
                    element={<V2SettingsDataImportPage />}
                  />
                  <Route
                    path="demo-import"
                    element={
                      <AbilityRoute action="manage" subject="all">
                        <V2SettingsDemoCatalogPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="demo-catalog/:id"
                    element={
                      <AbilityRoute action="manage" subject="all">
                        <V2SettingsDemoCatalogDetailPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="backup-reset"
                    element={<V2SettingsBackupResetPage />}
                  />
                  <Route path="explore" element={<V2SettingsExplorePage />} />
                  <Route
                    path="account"
                    element={
                      <AbilityRoute action="update" subject="Account">
                        <V2SettingsAccountPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="businesses"
                    element={
                      <AbilityRoute action="update" subject="Account">
                        <V2SettingsBusinessesPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="members"
                    element={
                      <AbilityRoute action="update" subject="Account">
                        <V2SettingsMembersPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="members/:userId"
                    element={
                      <AbilityRoute action="update" subject="Account">
                        <V2SettingsMemberDetailPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="permissions"
                    element={
                      <AbilityRoute action="update" subject="Account">
                        <V2SettingsPermissionsPage />
                      </AbilityRoute>
                    }
                  />
                  <Route path="users" element={<V2SettingsTeamPage />} />
                  <Route
                    path="adva/accounts"
                    element={
                      <AbilityRoute action="manage" subject="all">
                        <V2AdvaAccountsPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="adva/accounts/:id/users"
                    element={
                      <AbilityRoute action="manage" subject="all">
                        <V2AdvaAccountUsersPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="adva/accounts/:id/businesses"
                    element={
                      <AbilityRoute action="manage" subject="all">
                        <V2AdvaAccountBusinessesPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="adva/users"
                    element={
                      <AbilityRoute action="manage" subject="all">
                        <V2AdvaUsersPage />
                      </AbilityRoute>
                    }
                  />
                  <Route
                    path="adva/impersonate"
                    element={
                      <AbilityRoute action="manage" subject="all">
                        <V2AdvaImpersonatePage />
                      </AbilityRoute>
                    }
                  />
                </Route>
              </Routes>
            </Router>
          </AbilityProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function V2RedirectAll() {
  const { pathname, search, hash } = useLocation();
  const target = pathname.replace(/^\/v2(?=\/|$)/, "") || "/";
  return <Navigate to={`${target}${search}${hash}`} replace />;
}

export default App;
